import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Button from '../components/Button';
import { RailFrame } from '../components/AdminShell';
import './AdminDashboard.css';
import '../components/AdminShell.css';
import '../components/lists/Lists.css';
import './WebinarsPage.css';

// The API collapses athlete-side roles (base_user / player_parent) to
// "athlete" via coalesce_role, so this only needs the canonical values.
const ROLE_LABELS = {
  athlete: 'ATHLETE',
  guardian: 'GUARDIAN',
  coach: 'COACH',
  partner: 'PARTNER',
  admin: 'ADMIN',
  internal_staff: 'INTERNAL STAFF',
  marketing: 'MARKETING',
};

// Chip tone per role (adm-chip classes): athlete green, guardian amber, everyone else neutral.
const ROLE_CHIP = { athlete: 'adm-chip--green', guardian: 'adm-chip--amber' };


const US_TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern (ET)' },
  { id: 'America/Chicago', label: 'Central (CT)' },
  { id: 'America/Denver', label: 'Mountain (MT)' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { id: 'America/Anchorage', label: 'Alaska (AKT)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];
const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'; }
  catch { return 'America/Chicago'; }
})();

const EMPTY_FORM = {
  title: '', description: '', start_at: '', end_at: '', timezone: BROWSER_TZ,
  video_conference_link: '', price_cents: 0, capacity: '',
  slug: '', public_registration_enabled: false,
};

const fmtPrice = (cents) => `$${((parseInt(cents) || 0) / 100).toFixed(2)}`;

// The admin endpoint returns start_at as a naive wall-clock string in the
// webinar's own timezone. Render those clock components as-is (do NOT pass
// timeZone to toLocaleString, which would re-interpret them) and append the
// zone label.
const fmtWhen = (iso, tz) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const when = d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  const tzLabel = (US_TIMEZONES.find((z) => z.id === tz)?.label) || tz || '';
  return tzLabel ? `${when} · ${tzLabel}` : when;
};

// "Wed Nov 12 · 7:00 PM · Central (CT) · Free" — the detail header's hint line.
const fmtHint = (w) => {
  const d = new Date(w.start_at);
  const day = isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const tz = (US_TIMEZONES.find((z) => z.id === w.timezone)?.label) || w.timezone || '';
  const price = (parseInt(w.price_cents) || 0) > 0 ? fmtPrice(w.price_cents) : 'Free';
  return [day, time, tz, price].filter(Boolean).join(' · ');
};

// "Zoom" / "Teams" / "Meet" when the link host is recognisable, else the bare host.
const linkService = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host.includes('zoom')) return 'Zoom';
    if (host.includes('teams')) return 'Teams';
    if (host.includes('meet.google')) return 'Meet';
    return host;
  } catch { return ''; }
};

const isPast = (w) => {
  const t = new Date(w.start_at).getTime();
  return !isNaN(t) && t < Date.now();
};

function WebinarsContent() {
  const { user, API_URL } = useAuth();
  const [webinars, setWebinars] = useState(null); // null = still loading
  const [loadError, setLoadError] = useState(false);
  const [indexFilter, setIndexFilter] = useState('');
  const [regsFilter, setRegsFilter] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [regs, setRegs] = useState(null); // null = loading

  const isAdmin = user && (user.role === 'admin' || user.role === 'internal_staff');
  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  useEffect(() => { fetchWebinars(); }, []);

  const fetchWebinars = async () => {
    try {
      const r = await axios.get(`${API_URL}/webinars`, getAxiosConfig());
      setWebinars(r.data);
      setLoadError(false);
    } catch { setLoadError(true); }
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); };

  const openCreate = () => { resetForm(); setShowModal(true); };
  const closeModal = () => { setShowModal(false); resetForm(); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      title: w.title || '', description: w.description || '',
      start_at: (w.start_at || '').slice(0, 16), end_at: (w.end_at || '').slice(0, 16),
      timezone: w.timezone || BROWSER_TZ, video_conference_link: w.video_conference_link || '',
      price_cents: w.price_cents || 0, capacity: w.capacity ?? '',
      slug: w.slug || '', public_registration_enabled: !!w.public_registration_enabled,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (form.public_registration_enabled && !form.video_conference_link.trim()) {
      setMessage('A video conference link is required to publish.');
      return;
    }
    const payload = {
      ...form,
      price_cents: parseInt(form.price_cents) || 0,
      capacity: form.capacity === '' ? null : parseInt(form.capacity),
      public_registration_enabled: !!form.public_registration_enabled,
    };
    try {
      if (editing) await axios.put(`${API_URL}/webinars/${editing.id}`, payload, getAxiosConfig());
      else await axios.post(`${API_URL}/webinars`, payload, getAxiosConfig());
      setMessage(editing ? 'Webinar updated!' : 'Webinar created!');
      closeModal(); fetchWebinars();
    } catch (e) {
      const d = e.response?.data?.detail;
      setMessage(d?.code === 'video_link_required'
        ? 'A video conference link is required to publish.'
        : (typeof d === 'string' ? d : 'Error saving webinar.'));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This removes its registrations.`)) return;
    try { await axios.delete(`${API_URL}/webinars/${w.id}`, getAxiosConfig()); fetchWebinars(); }
    catch { setMessage('Could not delete.'); }
  };

  const exportCsv = async (w) => {
    const r = await axios.get(`${API_URL}/webinars/${w.id}/registrations/export`,
      { ...getAxiosConfig(), responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `${w.slug || w.title}_registrations.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const publicUrl = (slug) => `${window.location.origin}/webinar/${slug}`;

  // Index groups. Past wins over published/draft so an old webinar never sits under PUBLISHED.
  const groups = useMemo(() => {
    const q = indexFilter.trim().toLowerCase();
    const all = (webinars || []).filter((w) => !q || (w.title || '').toLowerCase().includes(q));
    return [
      { label: 'PUBLISHED', items: all.filter((w) => !isPast(w) && w.public_registration_enabled) },
      { label: 'DRAFTS', items: all.filter((w) => !isPast(w) && !w.public_registration_enabled) },
      { label: 'PAST', items: all.filter(isPast) },
    ];
  }, [webinars, indexFilter]);

  // Selection lives in ?webinar=ID. A missing or stale id falls back to the first item shown.
  const selectedId = Number(searchParams.get('webinar')) || null;
  const selected = useMemo(() => {
    const all = webinars || [];
    return all.find((w) => w.id === selectedId) || groups.flatMap((g) => g.items)[0] || all[0] || null;
  }, [webinars, groups, selectedId]);
  const selectWebinar = (id) => setSearchParams({ webinar: String(id) }, { replace: true });

  // Registrations for the selected webinar.
  const selId = selected?.id;
  useEffect(() => {
    if (!selId) return undefined;
    let cancelled = false;
    setRegs(null); setRegsFilter(''); setMenuOpen(false);
    axios.get(`${API_URL}/webinars/${selId}/registrations`, getAxiosConfig())
      .then((r) => { if (!cancelled) setRegs(r.data); })
      .catch(() => { if (!cancelled) setRegs([]); });
    return () => { cancelled = true; };
  }, [selId, API_URL]); // getAxiosConfig only reads localStorage

  const showMatch = Array.isArray(regs) && regs.some((r) => r.match !== undefined);
  const shownRegs = (regs || []).filter((r) => {
    const q = regsFilter.trim().toLowerCase();
    return !q || `${r.first_name} ${r.last_name} ${r.email} ${r.phone}`.toLowerCase().includes(q);
  });
  const verifiedCount = (regs || []).filter((r) => r.phone_verified).length;
  const service = selected?.video_conference_link ? linkService(selected.video_conference_link) : '';

  return (
    <div className="ls-grid">
      <aside className="adm__card ls-index">
        <div className="ls-index__head">
          <h2 className="ls-index__title">Webinars</h2>
          <button type="button" className="ls-btn ls-btn--primary ls-btn--tiny" onClick={openCreate}>+ New webinar</button>
        </div>
        <input className="ls-find" type="search" placeholder="Find a webinar…" aria-label="Find a webinar"
          value={indexFilter} onChange={(e) => setIndexFilter(e.target.value)} />
        {webinars === null && !loadError && <p className="ls-empty">Loading…</p>}
        {loadError && <p className="ls-empty adm-error" role="alert">Could not load webinars.</p>}
        {webinars && groups.filter((g) => g.items.length).map((g) => (
          <div className="ls-group" key={g.label}>
            <div className="ls-group__label">{g.label}</div>
            {g.items.map((w) => (
              <button key={w.id} type="button"
                className={`ls-irow ${selected?.id === w.id ? 'ls-irow--active' : ''}`}
                aria-current={selected?.id === w.id ? 'true' : undefined}
                onClick={() => selectWebinar(w.id)}>
                <span className="ls-irow__text">
                  <span className="ls-irow__title">{w.title}</span>
                  <span className="ls-irow__sub">{fmtWhen(w.start_at, w.timezone)}</span>
                </span>
                <span className="ls-irow__count">{w.registration_count}</span>
              </button>
            ))}
          </div>
        ))}
      </aside>

      <section className="adm__card ls-open">
        {message && <div className="ls-banner" role="status">{message}</div>}
        {webinars && webinars.length === 0 && (
          <div className="ls-empty ls-empty--pane">
            <p>No webinars yet. Create your first webinar to publish a public registration page.</p>
            <button type="button" className="ls-btn ls-btn--primary" onClick={openCreate}>+ New webinar</button>
          </div>
        )}
        {selected && (
          <>
            <div className="ls-head">
              <div className="ls-head__main">
                <div className="ls-head__meta">
                  <span className={`ls-chip ${selected.public_registration_enabled ? '' : 'wb-chip--draft'}`}>
                    {selected.public_registration_enabled ? 'Published' : 'Draft'}
                  </span>
                  <span className="ls-head__hint">{fmtHint(selected)}</span>
                </div>
                <h2 className="wb-title">{selected.title}</h2>
              </div>
              <div className="ls-head__actions">
                {selected.slug && selected.public_registration_enabled && (
                  <span className="ls-link-pill">
                    <code className="ls-link-pill__code">/webinar/{selected.slug}</code>
                    <button type="button" className="ls-btn ls-btn--tiny"
                      onClick={() => navigator.clipboard?.writeText(publicUrl(selected.slug))}>Copy</button>
                  </span>
                )}
                <button type="button" className="ls-btn ls-btn--dark" onClick={() => openEdit(selected)}>Edit</button>
                <div className="ls-menu-wrap">
                  <button type="button" className="ls-btn ls-btn--round" aria-label="More actions"
                    aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>⋯</button>
                  {menuOpen && (
                    <div className="ls-menu" role="menu">
                      <button type="button" role="menuitem" className="ls-menu__item ls-menu__item--danger"
                        onClick={() => { setMenuOpen(false); remove(selected); }}>Delete</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="wb-tiles">
              <div className="wb-tile">
                <span className="wb-tile__label">Registered</span>
                <span className="wb-tile__value">{selected.registration_count}</span>
              </div>
              <div className="wb-tile">
                <span className="wb-tile__label">Capacity</span>
                <span className="wb-tile__value">{selected.capacity ?? 'No limit'}</span>
              </div>
              <div className="wb-tile">
                <span className="wb-tile__label">Phone verified</span>
                <span className="wb-tile__value">{regs === null ? '…' : verifiedCount}</span>
              </div>
              <div className="wb-tile">
                <span className="wb-tile__label">Video link</span>
                <span className={`wb-tile__value ${selected.video_conference_link ? 'wb-tile__value--ok' : 'wb-tile__value--warn'}`}>
                  {selected.video_conference_link ? `Set${service ? ` · ${service}` : ''}` : 'Missing'}
                </span>
              </div>
            </div>

            <div className="wb-regs">
              <div className="wb-regs__bar">
                <h3 className="wb-regs__title">Registrations</h3>
                <input className="adm-input wb-regs__find" type="search" placeholder="Search registrants"
                  aria-label="Search registrants" value={regsFilter} onChange={(e) => setRegsFilter(e.target.value)} />
                {isAdmin && (
                  <button type="button" className="ls-btn ls-btn--tiny" onClick={() => exportCsv(selected)}>Export CSV</button>
                )}
              </div>
              {regs === null ? <p className="adm-loading">Loading registrations…</p>
                : shownRegs.length === 0 ? <p className="adm-empty">{regs.length === 0 ? 'No registrations yet.' : 'No one matches that search.'}</p>
                : (
                  <div className="adm-tablewrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>NAME</th><th>EMAIL</th><th>PHONE</th>
                          {showMatch && <th>ACCOUNT MATCH</th>}
                          <th>VERIFIED</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownRegs.map((r) => (
                          <tr key={r.id}>
                            <td className="wb-cell--name">{r.first_name} {r.last_name}</td>
                            <td>{r.email}</td>
                            <td>{r.phone}</td>
                            {showMatch && (
                              <td>
                                {r.match ? (
                                  <span className="wb-match">
                                    <span className={`adm-chip ${ROLE_CHIP[r.match.role] || ''}`}>{ROLE_LABELS[r.match.role] || r.match.role}</span>
                                    {r.match.athletes.length > 0 && <span>→ {r.match.athletes.map((a) => a.name).join(', ')}</span>}
                                    {r.concierge && <span className="adm-chip adm-chip--amber">CONCIERGE</span>}
                                  </span>
                                ) : <span className="wb-nomatch">No matching account</span>}
                              </td>
                            )}
                            <td><span className={`adm-chip ${r.phone_verified ? 'adm-chip--green' : ''}`}>{r.phone_verified ? 'VERIFIED' : 'UNVERIFIED'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </>
        )}
      </section>

      {showModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="webinar-modal-title" onClick={closeModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal} aria-label="Close">&times;</button>
            <div className="modal-title" id="webinar-modal-title">
              {editing ? 'Edit Webinar' : 'New Webinar'}
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Title *</label>
                <input type="text" className="form-field__input" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Recruiting 101: Getting Noticed" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Description</label>
                <textarea className="form-field__input" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What attendees will learn..." rows={3} style={{ resize: 'vertical' }} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Start *</label>
                <input type="datetime-local" className="form-field__input" value={form.start_at}
                  onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
              </div>
              <div className="form-field">
                <label className="form-field__label">End (optional)</label>
                <input type="datetime-local" className="form-field__input" value={form.end_at}
                  min={form.start_at || undefined}
                  onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Timezone *</label>
                <select className="form-field__input" value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
                  {form.timezone && !US_TIMEZONES.some((z) => z.id === form.timezone) && (
                    <option value={form.timezone}>{form.timezone}</option>
                  )}
                  {US_TIMEZONES.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                </select>
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  The webinar's local time — shown in this zone to everyone, nationwide.
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Video conference link</label>
                <input type="url" className="form-field__input" value={form.video_conference_link}
                  onChange={(e) => setForm({ ...form, video_conference_link: e.target.value })}
                  placeholder="https://zoom.us/j/..." />
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Required before you can publish. Sent to registrants by email — never shown publicly.
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Price (cents — 0 = free)</label>
                <input type="number" min="0" className="form-field__input" value={form.price_cents}
                  onChange={(e) => setForm({ ...form, price_cents: e.target.value })} placeholder="0" />
                {parseInt(form.price_cents) > 0 && (
                  <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                    = {fmtPrice(form.price_cents)}
                  </span>
                )}
              </div>
              <div className="form-field">
                <label className="form-field__label">Capacity (optional)</label>
                <input type="number" min="0" className="form-field__input" value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="No limit" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">URL Slug</label>
                <input type="text" className="form-field__input" value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="auto from title if blank (e.g. recruiting-101)" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!form.public_registration_enabled}
                    onChange={(e) => setForm({ ...form, public_registration_enabled: e.target.checked })} />
                  Public registration enabled
                </label>
                {editing && form.slug && form.public_registration_enabled && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Public link:&nbsp;
                    <code>{publicUrl(form.slug)}</code>
                    <Button variant="ghost" size="sm" style={{ marginLeft: 8 }}
                      onClick={() => navigator.clipboard?.writeText(publicUrl(form.slug))}>
                      Copy
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <Button variant="primary" onClick={save}>
                {editing ? 'Save Changes' : 'Create Webinar'}
              </Button>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin and partner only (the route is role-gated), so the rail is always present.
export default function WebinarsPage() {
  return (
    <RailFrame active="webinars">
      <WebinarsContent />
    </RailFrame>
  );
}
